
import axios from "axios";
import type {
  ApiCollectionResponse,
  ApiItemResponse,
  BasedComponent,
  BasedComponentPayload,
  BasedContent,
  BasedContentPayload,
  BasedContentUpdatePayload,
} from "../interfaces/basedComponents";
import type {
  User,
  Ticket,
  TicketCollectionResponse,
  TicketItemResponse,
  CommentItemResponse,
  CommentCollectionResponse,
} from "../interfaces/tickets";
import type { NotificationApiResponse } from "../interfaces/notifications";

export const API_URL = process.env.VUE_APP_API_URL ?? "http://10.0.0.132:8000/api";

type RetriableRequestConfig = {
  _retry?: boolean;
  headers?: Record<string, string>;
  url?: string;
};

let refreshRequest: Promise<string | null> | null = null;

const clearAuthState = () => {
  localStorage.removeItem("user-token");
  localStorage.removeItem("refresh-token");
  localStorage.removeItem("user-info");
};

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem("refresh-token");
  if (!refreshToken) return null;

  if (!refreshRequest) {
    refreshRequest = axios
      .post(`${API_URL}/refresh`, { refresh_token: refreshToken })
      .then((response) => {
        const { access_token, refresh_token } = response.data?.data ?? {};

        if (!access_token) return null;

        localStorage.setItem("user-token", access_token);
        if (refresh_token) {
          localStorage.setItem("refresh-token", refresh_token);
        }

        return access_token as string;
      })
      .catch((refreshError) => {
        console.error("[auth] refresh failed", refreshError);
        return null;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
};

export const apiClient = axios.create({
  baseURL: API_URL,
});

// 1. Request Interceptor: Attach access_token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("user-token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log("[http] request", config.method?.toUpperCase(), config.url, {
    hasAuthHeader: !!config.headers?.Authorization,
  });
  return config;
});

export const getMe = () => {
  console.log("[api] getMe() called → GET /me");
  return apiClient.get("/me");
};

// 2. Response Interceptor: Handle Refresh logic
apiClient.interceptors.response.use(
  (response) => {
    if (response.config.url === "/me") {
      console.log("[http] response /me", {
        status: response.status,
        fromRetry: (response.config as { _retry?: boolean })._retry === true,
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = (error.config ?? {}) as RetriableRequestConfig;

    console.log("[http] response error", {
      url: originalRequest?.url,
      status: error.response?.status,
    });

    const isUnauthorized = error.response?.status === 401;
    const isRefreshCall = originalRequest.url?.includes("/refresh") === true;

    if (isUnauthorized && !originalRequest._retry && !isRefreshCall) {
      console.log("[auth] 401 received, attempting token refresh for", originalRequest.url);
      originalRequest._retry = true;

      const newAccessToken = await refreshAccessToken();
      if (newAccessToken) {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        console.log("[auth] refresh succeeded, retrying original request", originalRequest.url);
        return apiClient(originalRequest);
      }

      console.error("[auth] refresh failed, clearing session");
      clearAuthState();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// 3. Logout Function
export const logout = async () => {
  try {
    await apiClient.post('/logout');
  } catch (err) {
    console.error('Logout API error:', err);
  }
  
  localStorage.removeItem('user-token');
  localStorage.removeItem('refresh-token');
  localStorage.removeItem('user-info');
};

// 4. Login Function (Updated for your JSON structure)
export const login = async (email: string, password: string) => {
  try {
    const response = await apiClient.post("/login", { email, password });
    
    // Accessing nested "data" from your Postman response
    const payload = response.data.data;

    if (payload.access_token) {
      localStorage.setItem("user-token", payload.access_token);
      localStorage.setItem("refresh-token", payload.refresh_token);
      // If backend sends user info, store it; otherwise use a placeholder
      localStorage.setItem("user-info", JSON.stringify(payload.user || { email }));
    }
    return true;
  } catch (error) {
    console.error("Login Error:", error);
    throw error;
  }
};

// Based Components CRUD
export const getBasedComponents = () =>
  apiClient.get<ApiCollectionResponse<BasedComponent>>("/based-components/list");

export const getBasedComponent = (id: string) =>
  apiClient.get<ApiItemResponse<BasedComponent>>(`/based-components/show/${id}`);

export const createBasedComponent = (data: BasedComponentPayload) =>
  apiClient.post<ApiItemResponse<BasedComponent>>("/based-components/store", data);

export const updateBasedComponent = (id: string, data: Partial<BasedComponentPayload>) =>
  apiClient.patch<ApiItemResponse<BasedComponent>>(`/based-components/update/${id}`, data);

export const deleteBasedComponent = (id: string) =>
  apiClient.delete<{ success: boolean }>(`/based-components/delete/${id}`);

// Based Components Contents CRUD
export const getBasedContents = () =>
  apiClient.get<ApiCollectionResponse<BasedContent>>("/based-components-contents/list");

export const getBasedContent = (id: string) =>
  apiClient.get<ApiItemResponse<BasedContent>>(`/based-components-contents/show/${id}`);

// Shared content made by other users and marked active/published
export const getOtherUsersActiveBasedContents = () =>
  apiClient.get<ApiCollectionResponse<BasedContent>>("/based-components-contents/other-users-active");

export const createBasedContent = (data: BasedContentPayload) =>
  apiClient.post<ApiItemResponse<BasedContent>>("/based-components-contents/store", data);

export const updateBasedContent = (id: string, data: BasedContentUpdatePayload) =>
  apiClient.put<ApiItemResponse<BasedContent>>(`/based-components-contents/update/${id}`, data);

export const deleteBasedContent = (id: string) =>
  apiClient.delete<{ success: boolean }>(`/based-components-contents/delete/${id}`);

// Sidebar menu (published only)
export const getSidebarMenu = () =>
  apiClient.get<ApiCollectionResponse<BasedComponent>>("/based-components/sidebar-menu");

// Ticket System CRUD
export const getTickets = (params?: { type?: string; status?: string; search?: string; issue_type?: string }) =>
  apiClient.get<TicketCollectionResponse>("/tickets/list", { params });

export const getMyTickets = (params?: { type?: string; status?: string; search?: string; issue_type?: string }) =>
  apiClient.get<TicketCollectionResponse>("/tickets/my-tickets", { params });

export const getAssignedTickets = (params?: { type?: string; status?: string; search?: string; issue_type?: string }) =>
  apiClient.get<TicketCollectionResponse>("/tickets/assigned", { params });

export const getTicketHistory = (params?: { type?: string; status?: string; search?: string; issue_type?: string }) =>
  apiClient.get<TicketCollectionResponse>("/tickets/history", { params });

export const getTicketAgents = () =>
  apiClient.get<ApiCollectionResponse<User>>("/tickets/agents");

export const createTicket = (formData: FormData) =>
  apiClient.post<TicketItemResponse>("/tickets/store", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const getTicket = (id: string) =>
  apiClient.get<TicketItemResponse>(`/tickets/show/${id}`);

export const updateTicket = (id: string, data: Partial<Ticket>) =>
  apiClient.patch<TicketItemResponse>(`/tickets/update/${id}`, data);

export const assignTicket = (id: string, userId?: string, userIds?: string[]) =>
  apiClient.post<TicketItemResponse>(`/tickets/assign/${id}`, {
    user_id: userId,
    user_ids: userIds,
  });

export const unassignTicket = (id: string, userId?: string, userIds?: string[]) =>
  apiClient.post<TicketItemResponse>(`/tickets/unassign/${id}`, {
    user_id: userId,
    user_ids: userIds,
  });

export const deleteTicket = (id: string) =>
  apiClient.delete<{ message: string }>(`/tickets/delete/${id}`);

// Ticket Engagement
export const voteTicket = (id: string) =>
  apiClient.post<{ voted: boolean; likes_count: number }>(`/tickets/vote/${id}`);

// Comments
export const createComment = (
  ticketId: string,
  data: { body: string; parent_id?: string | null } | FormData
) =>
  apiClient.post<CommentItemResponse>(`/tickets/${ticketId}/comments/store`, data, data instanceof FormData
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : undefined);

export const getTicketComments = (
  ticketId: string,
  params?: { limit?: number; cursor?: string }
) =>
  apiClient.get<CommentCollectionResponse>(`/tickets/${ticketId}/comments`, { params });

export const likeComment = (id: string) =>
  apiClient.post<{ liked: boolean; likes_count: number }>(`/comments/like/${id}`);

export const updateComment = (id: string, data: { body: string }) =>
  apiClient.patch<CommentItemResponse>(`/comments/update/${id}`, data);

export const deleteComment = (id: string) =>
  apiClient.delete<{ message: string; deleted: number }>(`/comments/delete/${id}`);

// Notifications
export const getNotifications = (params?: { page?: number; per_page?: number }) =>
  apiClient.get<NotificationApiResponse>("/notifications", { params });

export const markNotificationAsRead = (id: string) =>
  apiClient.post(`/notifications/${id}/read`);

export const markAllNotificationsAsRead = () =>
  apiClient.post("/notifications/read-all");

export const deleteNotification = (id: string) =>
  apiClient.delete(`/notifications/${id}`);
